import React, { useEffect, useState } from "react";
import cn from "classnames";
import { useTranslation } from "@litres-libs/i18n";
import { useAppSelector } from "@litres-core/store";

import { SmartLink } from "@litres-modules/smart-link";
import { hasWindow } from "@litres/utils";
import type { TabsType, ThemesType } from "./types";
import { TABS } from "./constants";
import { Counter } from "./Counter";
import { trackerSendHeaderClick } from "../../utils/analytics";

import { EMyBooksRoute } from "../../utils/enums";
import styles from "./Tab.module.scss";

type EventType = React.MouseEvent<HTMLElement>;

export interface ITabProps {
  theme?: ThemesType;
  type: TabsType;
  count?: number | undefined | null;
  classNames?: string[];
  onClick?: (event: EventType) => void;
  isActive?: boolean;
  numberTitle?: number | null;
}

export const Tab = ({
  type,
  theme = "default",
  count,
  classNames = [],
  isActive = false,
  numberTitle,
  onClick,
}: ITabProps): JSX.Element => {
  const { t } = useTranslation();
  // проверка пда
  const isPDA = useAppSelector(state => state.browser.pda);
  const [url, setUrl] = useState(TABS[type].href);

  const mainClass = "tab";
  const themeModifier = theme === "default" ? "" : `_${theme}`;
  const bem = (element?: string, modifier?: string) =>
    styles[
      `${mainClass}${themeModifier}${element ? `__${element}` : ""}${
        modifier ? `_${modifier}` : ""
      }`
    ];
  const handleTabClick = (event: EventType, name: string) => {
    if (isActive) {
      event.preventDefault();
      return false;
    }
    trackerSendHeaderClick(name, hasWindow ? window.location.href : "");
    if (onClick) {
      onClick(event);
    }
  };

  useEffect(() => {
    if (type === "myBooks" && !isPDA) {
      setUrl(EMyBooksRoute.PURCHASED);
    } else {
      setUrl(TABS[type].href);
    }
  }, [type, isPDA]);

  return (
    <div
      className={cn(bem(), ...classNames)}
      data-testid={`tab-${TABS[type].label}`}
      id={`tab-${TABS[type].label}`}
    >
      <SmartLink
        onClick={event => {
          handleTabClick(event, TABS[type].title);
        }}
        href={url}
        className={cn(bem("link"), {
          [bem("link", "active")]: isActive,
        })}
        data-testid="tab__link"
      >
        <div className={bem("wrapper")}>
          {typeof numberTitle === "number" && (
            <span className={bem("wrapper__number")}>{numberTitle}</span>
          )}
          <div
            className={cn(bem("wrapper__logo"), {
              [bem("wrapper__logo", "small")]: !!numberTitle,
            })}
          >
            {TABS[type].icon}
            <Counter>{count}</Counter>
          </div>
        </div>
        <p className={bem("title")}>{t(TABS[type].title)}</p>
      </SmartLink>
    </div>
  );
};
